// src/pages/tenant/TenantDashboardPage.tsx
// Tenant dashboard page - overview of current tenant stats, usage, activity.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useTenantTenant, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map tenant type enum to human-readable label. */
function typeLabel(type: string): string {
  switch (type) {
    case 'TENANT_TYPE_PERSONAL':
      return 'Personal';
    case 'TENANT_TYPE_TEAM':
      return 'Team';
    case 'TENANT_TYPE_ORGANIZATION':
      return 'Organization';
    case 'TENANT_TYPE_ENTERPRISE':
      return 'Enterprise';
    default:
      return type?.replace('TENANT_TYPE_', '') || 'Unknown';
  }
}

type BadgeVariant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error';

/** Map tenant type to badge variant. */
function typeVariant(type: string): BadgeVariant {
  switch (type) {
    case 'TENANT_TYPE_PERSONAL':
      return 'secondary';
    case 'TENANT_TYPE_TEAM':
      return 'default';
    case 'TENANT_TYPE_ORGANIZATION':
      return 'primary';
    case 'TENANT_TYPE_ENTERPRISE':
      return 'warning';
    default:
      return 'secondary';
  }
}

/** Map tenant status to badge variant. */
function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'TENANT_STATUS_ACTIVE':
      return 'success';
    case 'TENANT_STATUS_SUSPENDED':
      return 'error';
    case 'TENANT_STATUS_PENDING':
      return 'warning';
    case 'TENANT_STATUS_ARCHIVED':
      return 'secondary';
    default:
      return 'secondary';
  }
}

/** Map tenant status to human-readable label. */
function statusLabel(status: string): string {
  switch (status) {
    case 'TENANT_STATUS_ACTIVE':
      return 'Active';
    case 'TENANT_STATUS_SUSPENDED':
      return 'Suspended';
    case 'TENANT_STATUS_PENDING':
      return 'Pending';
    case 'TENANT_STATUS_ARCHIVED':
      return 'Archived';
    default:
      return status?.replace('TENANT_STATUS_', '') || 'Unknown';
  }
}

/** Percentage of usage (clamped to 0-100). */
function usagePercent(current: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(Math.round((current / limit) * 100), 100);
}

/** Color class for usage bar based on threshold. */
function usageBarColor(percent: number): string {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 70) return 'bg-yellow-500';
  return 'bg-green-500';
}

/** Text color class for usage percentage label. */
function usageTextColor(percent: number): string {
  if (percent >= 90) return 'text-red-600';
  if (percent >= 70) return 'text-yellow-600';
  return 'text-green-600';
}

/** Format large numbers with K/M suffixes. */
function formatNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

/** Format bytes to human-readable storage. */
function formatStorage(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${bytes} B`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantDashboardPage - overview of current tenant stats, usage, and activity.
 * Route: /tenants/:id/dashboard
 */
export const TenantDashboardPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTenant();
  const crud = useTenantTenant();
  const { t } = useI18n();

  const [tenant, setTenant] = useState<any>(null);
  const [quota, setQuota] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [entitlements, setEntitlements] = useState<any[]>([]);

  useEffect(() => {
    if (!id) return;

    // Load tenant details
    crud.GetTenant(id).then((res: any) => {
      setTenant(res ?? null);
    }).catch(() => {});

    // Load tenant quota
    store.GetTenantQuota(id).then((res: any) => {
      setQuota(res ?? null);
    }).catch(() => {});

    // Load tenant analytics
    store.GetTenantAnalytics(id).then((res: any) => {
      setAnalytics(res?.analytics ?? null);
    }).catch(() => {});

    // Load tenant entitlements
    store.GetTenantEntitlements(id).then((res: any) => {
      const items = res?.entitlements?.product_entitlements;
      setEntitlements(Array.isArray(items) ? items : []);
    }).catch(() => {});

    return () => {
      crud.clearCurrent();
    };
  }, [id]);

  // Derive display values from tenant
  const tenantName = tenant?.name ?? '';
  const tenantStatus = tenant?.status ?? 'TENANT_STATUS_ACTIVE';
  const tenantType = tenant?.type ?? '';
  const tenantRegion = tenant?.region ?? '';

  // Derive stats from analytics
  const totalMembers = analytics?.total_members ?? tenant?.member_count ?? 0;
  const activeMembers = analytics?.active_members ?? 0;
  const storageUsed = analytics?.storage_bytes_used ?? 0;
  const apiCalls = analytics?.total_api_calls ?? 0;
  const membersTrend = analytics?.members_trend ?? null;
  const storageTrend = analytics?.storage_trend ?? null;
  const apiCallsTrend = analytics?.api_calls_trend ?? null;

  // Derive quota limits (GetTenantQuota returns { limits, usage })
  const maxUsers = quota?.limits?.max_users ?? 0;
  const maxStorage = quota?.limits?.max_storage_bytes ?? 0;
  const maxApiCalls = quota?.limits?.max_api_calls_per_month ?? 0;
  const maxApps = quota?.limits?.max_apps ?? 0;
  const currentUsers = quota?.limits?.current_users ?? quota?.usage?.users_count ?? totalMembers;
  const currentStorage = quota?.limits?.current_storage_bytes ?? quota?.usage?.storage_bytes_used ?? storageUsed;
  const currentApiCalls = quota?.limits?.current_api_calls_this_month ?? quota?.usage?.api_calls_count ?? apiCalls;
  const currentApps = quota?.limits?.current_apps ?? quota?.usage?.apps_count ?? 0;

  if ((store.isLoading || crud.isLoadingItem) && !tenant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if ((crud.error || store.error) && !tenant) {
    return (
      <div className="space-y-2">
        <ErrorAlert error={crud.error ?? store.error} />
        <Link to="/tenants" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        {tenantName && (
          <>
            <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
              {tenantName}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
          </>
        )}
        <span className="text-foreground">{t('tenant.dashboard.title')}</span>
      </nav>

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Tenant avatar */}
          {tenant?.logo_url ? (
            <img
              src={tenant.logo_url}
              alt={tenantName}
              className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold text-primary">
                {(tenantName || '?')[0]?.toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-foreground">{tenantName}</h1>
              <Badge variant={statusVariant(tenantStatus)} size="sm">
                {statusLabel(tenantStatus)}
              </Badge>
              {tenantType && (
                <Badge variant={typeVariant(tenantType)} size="sm">
                  {typeLabel(tenantType)}
                </Badge>
              )}
            </div>
            {tenantRegion && (
              <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
                {tenantRegion}
              </p>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/tenants/${id}/invite`)}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
            </svg>
            {t('tenant.dashboard.inviteMembers')}
          </Button>
          <Link to={`/tenants/${id}/settings`}>
            <Button variant="secondary" size="sm">
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('tenant.dashboard.settings')}
            </Button>
          </Link>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/tenants/${id}/analytics`)}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            {t('tenant.dashboard.viewAnalytics')}
          </Button>
        </div>
      </div>

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Members */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              </div>
              {membersTrend != null && (
                <span className={`text-xs font-medium ${membersTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {membersTrend >= 0 ? '+' : ''}{membersTrend}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-foreground mt-3">{formatNumber(totalMembers)}</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('tenant.dashboard.totalMembers')}
            </p>
          </CardContent>
        </Card>

        {/* Active Members */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
              </div>
              {totalMembers > 0 && (
                <span className="text-xs font-medium text-muted-foreground">
                  {Math.round((activeMembers / totalMembers) * 100)}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-foreground mt-3">{formatNumber(activeMembers)}</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('tenant.dashboard.activeMembers')}
            </p>
          </CardContent>
        </Card>

        {/* Storage Used */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                </svg>
              </div>
              {storageTrend != null && (
                <span className={`text-xs font-medium ${storageTrend >= 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {storageTrend >= 0 ? '+' : ''}{storageTrend}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-foreground mt-3">{formatStorage(storageUsed)}</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('tenant.dashboard.storageUsed')}
            </p>
          </CardContent>
        </Card>

        {/* API Calls */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                </svg>
              </div>
              {apiCallsTrend != null && (
                <span className={`text-xs font-medium ${apiCallsTrend >= 0 ? 'text-blue-600' : 'text-green-600'}`}>
                  {apiCallsTrend >= 0 ? '+' : ''}{apiCallsTrend}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-foreground mt-3">{formatNumber(apiCalls)}</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('tenant.dashboard.apiCalls')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Usage section */}
      {(maxUsers > 0 || maxStorage > 0 || maxApiCalls > 0 || maxApps > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tenant.dashboard.usage')}</CardTitle>
            <CardDescription>
              {t('tenant.dashboard.usageDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Users quota */}
            {maxUsers > 0 && (() => {
              const percent = usagePercent(currentUsers, maxUsers);
              return (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                      <span className="text-sm font-medium text-foreground">
                        {t('tenant.dashboard.quotaUsers')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground font-medium">
                        {currentUsers} / {maxUsers}
                      </span>
                      <span className={`text-xs font-medium ${usageTextColor(percent)}`}>
                        {percent}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${usageBarColor(percent)}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Storage quota */}
            {maxStorage > 0 && (() => {
              const percent = usagePercent(currentStorage, maxStorage);
              return (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                      </svg>
                      <span className="text-sm font-medium text-foreground">
                        {t('tenant.dashboard.quotaStorage')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground font-medium">
                        {formatStorage(currentStorage)} / {formatStorage(maxStorage)}
                      </span>
                      <span className={`text-xs font-medium ${usageTextColor(percent)}`}>
                        {percent}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${usageBarColor(percent)}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* API Calls quota */}
            {maxApiCalls > 0 && (() => {
              const percent = usagePercent(currentApiCalls, maxApiCalls);
              return (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                      </svg>
                      <span className="text-sm font-medium text-foreground">
                        {t('tenant.dashboard.quotaApiCalls')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground font-medium">
                        {formatNumber(currentApiCalls)} / {formatNumber(maxApiCalls)}
                      </span>
                      <span className={`text-xs font-medium ${usageTextColor(percent)}`}>
                        {percent}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${usageBarColor(percent)}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Apps quota */}
            {maxApps > 0 && (() => {
              const percent = usagePercent(currentApps, maxApps);
              return (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                      </svg>
                      <span className="text-sm font-medium text-foreground">
                        {t('tenant.dashboard.quotaApps')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground font-medium">
                        {currentApps} / {maxApps}
                      </span>
                      <span className={`text-xs font-medium ${usageTextColor(percent)}`}>
                        {percent}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${usageBarColor(percent)}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Entitlements section */}
      {entitlements.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">
            {t('tenant.dashboard.entitlements')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {entitlements.map((entitlement: any) => {
              const entId = entitlement.product_slug ?? entitlement.entity?.id ?? entitlement.id;
              const productName = entitlement.product_name ?? entitlement.product_slug ?? '';
              const plan = entitlement.plan ?? entitlement.plan_slug ?? '';
              const features = Array.isArray(entitlement.features) ? entitlement.features : [];
              const entitlementStatus = entitlement.status ?? 'active';
              const iconUrl = entitlement.icon_url ?? entitlement.product_icon;
              const colorPrimary = entitlement.color_primary || '#6366f1';
              const initial = (productName || '?')[0]?.toUpperCase() ?? '?';

              return (
                <Card key={entId} className="flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      {iconUrl ? (
                        <img
                          src={iconUrl}
                          alt={productName}
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                          style={{ backgroundColor: colorPrimary }}
                        >
                          {initial}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-sm truncate">{productName}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {plan && (
                            <Badge variant="primary" size="sm">{plan}</Badge>
                          )}
                          {entitlementStatus === 'active' && (
                            <Badge variant="success" size="sm">
                              {t('tenant.dashboard.entitlementActive')}
                            </Badge>
                          )}
                          {entitlementStatus === 'trial' && (
                            <Badge variant="warning" size="sm">
                              {t('tenant.dashboard.entitlementTrial')}
                            </Badge>
                          )}
                          {entitlementStatus === 'expired' && (
                            <Badge variant="error" size="sm">
                              {t('tenant.dashboard.entitlementExpired')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  {features.length > 0 && (
                    <CardContent className="flex-1 pt-0 pb-3">
                      <ul className="space-y-1">
                        {features.slice(0, 5).map((feature: any, idx: number) => {
                          const featureName = typeof feature === 'string' ? feature : feature.name ?? feature.label ?? '';
                          return (
                            <li key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                              </svg>
                              <span>{featureName}</span>
                            </li>
                          );
                        })}
                        {features.length > 5 && (
                          <li className="text-xs text-muted-foreground pl-5.5">
                            +{features.length - 5} {t('tenant.dashboard.moreFeatures')}
                          </li>
                        )}
                      </ul>
                    </CardContent>
                  )}
                  <CardFooter className="pt-3 border-t border-border">
                    <Link to={`/settings/products/${entId}`} className="w-full">
                      <Button variant="secondary" size="sm" className="w-full">
                        {t('tenant.dashboard.manageProduct')}
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

export default TenantDashboardPage;
